import { UseQueryResult } from '@tanstack/react-query';

import { GetOneRosterUserResponse } from '@/actions/auth';
import { useImpersonation } from '@/hooks/useImpersonation';
import { useOneRosterAuthQuery } from '@/hooks/useOneRosterAuthQuery';
import { useParentStudents } from '@/hooks/useParentStudents';
import { UserRoles } from '@/lib/constants/auth.constants.ts';
import { OneRosterUser } from '@/types/user.oneroster';

import { AuthenticationContext, OneRosterAuth } from './auth.context';

export const AuthenticationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const oneRosterUserQuery = useOneRosterAuthQuery();
  const impersonationQuery = useImpersonation();
  const parentStudentsQuery = useParentStudents(
    oneRosterUserQuery.data?.user?.sourcedId
  );
  const auth = resolveContextValues(
    oneRosterUserQuery,
    impersonationQuery,
    parentStudentsQuery
  );

  return (
    <AuthenticationContext.Provider value={auth}>
      {children}
    </AuthenticationContext.Provider>
  );
};

function resolveContextValues(
  oneRosterUserQuery: UseQueryResult<GetOneRosterUserResponse, Error>,
  impersonationQuery: ReturnType<typeof useImpersonation>,
  parentStudentsQuery: ReturnType<typeof useParentStudents>
): OneRosterAuth {
  const data = oneRosterUserQuery.data;
  const error = oneRosterUserQuery.error;
  const impersonatedUser = impersonationQuery.impersonatedUser;
  const children = parentStudentsQuery.students;

  const isLoading =
    oneRosterUserQuery.isLoading ||
    impersonationQuery.isLoading ||
    parentStudentsQuery.isLoading;
  if (!data?.isAuthenticated) {
    return {
      user: null,
      isAuthenticated: false,
      roles: [],
      isAdmin: false,
      isAide: false,
      isParent: false,
      isSysAdmin: false,
      isImpersonated: false,
      isLoading,
      error,
      children: [],
      hasRole: () => false,
    };
  }

  const user = data?.user as OneRosterUser | null;
  const roles = user?.roles?.map((role) => role.role as UserRoles) ?? [];

  const isAdmin =
    roles.includes(UserRoles.SYSTEM_ADMINISTRATOR) ||
    roles.includes(UserRoles.ADMINISTRATOR);

  const isAide = roles.includes(UserRoles.AIDE);
  const isParent = roles.includes(UserRoles.PARENT);

  const isSysAdmin = roles.includes(UserRoles.SYSTEM_ADMINISTRATOR);

  const isImpersonated = !!impersonatedUser && (isAdmin || isAide);

  return {
    user: isImpersonated
      ? (impersonatedUser as unknown as OneRosterUser)
      : user,
    isAuthenticated: data?.isAuthenticated ?? false,
    roles,
    isAdmin,
    isAide,
    isParent,
    isSysAdmin,
    isImpersonated,
    isLoading,
    error,
    children: children || [],
    hasRole: (role: UserRoles) => {
      return isAdmin || roles.includes(role);
    },
  };
}
